import {
  AddressElement,
  PaymentElement,
  PaymentRequestButtonElement,
  useElements,
  useStripe
} from '@stripe/react-stripe-js';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useStartPaidSessionMutation } from '../../redux/api/sessions';
import { selectAuthData } from '../../redux/selectors/auth';
import { setIsButtonLoading } from '../../redux/reducers/chargingPointSlice';
import { setIsButtonLoadingForId } from '../../redux/reducers/chargingPointsSlice';

import Button from '../Utils/Button';
import FormInput from '../Utils/Form/FormInput';
import inputTypes from '../Utils/Form/FormInput/inputTypes';
import TabBar from '../Utils/TabBar';

import './style.scss';

const CheckoutForm = ({
  appearance,
  paymentIntent: paymentIntentData,
  cp_id,
  connector_id,
  onSuccess,
  clientSecret,
  price,
  currency,
  fromCharge
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { user: { email: existingUserEmail = '' } = {} } = useSelector(selectAuthData);
  const [paymentId, setPaymentId] = useState(null);
  const [user, setUser] = useState(null);
  const [userEmail, setUserEmail] = useState('');
  const [hasValidAddress, setHasValidAddress] = useState(false);
  const [hasValidPayment, setHasValidPayment] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [activeTabIndex, setActiveTabIndex] = useState(0); //Pay with wallet
  const tabButtons = [t('pay_with_wallet'), t('pay_with_card')];
  const baseFontSize = useRef(
    getComputedStyle(document.documentElement).getPropertyValue('font-size')
  );
  const onResize = () => {
    const fontSize = getComputedStyle(document.documentElement).getPropertyValue('font-size');
    if (fontSize !== baseFontSize.current) {
      baseFontSize.current = fontSize;
      const _appearance = JSON.parse(JSON.stringify(appearance));
      _appearance.variables.fontSizeBase = fontSize;
      elements && elements.update({ appearance: _appearance });
    }
  };

  useEffect(() => {
    onResize();
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [elements]);

  const [
    startPaidSession,
    { data: sessionData, isError, isSuccess, isLoading: isPaidSessionLoading }
  ] = useStartPaidSessionMutation();

  useEffect(() => {
    if (existingUserEmail !== userEmail) {
      setUserEmail(existingUserEmail);
    }
  }, [existingUserEmail]);

  // Redirect to session/:sessionId after payment
  useEffect(() => {
    if (isSuccess) {
      navigate(sessionData.sessionUrl);
    }
  }, [isSuccess]);

  // Check if user can pay with wallet
  useEffect(() => {
    if (stripe) {
      const pr = stripe.paymentRequest({
        country: 'US',
        currency: currency,
        total: {
          label: 'Demo total',
          amount: price
        },
        requestPayerName: true,
        requestPayerEmail: true
      });

      // Check the availability of the Payment Request API.
      pr.canMakePayment().then((result) => {
        if (result) {
          setActiveTabIndex(0);
          setPaymentRequest(pr);
        }
      });
    }
  }, [stripe]);
  useEffect(() => {
    if (paymentRequest) {
      paymentRequest.on('paymentmethod', async (ev) => {
        // Confirm the PaymentIntent without handling potential next actions (yet).
        const { paymentIntent, error: confirmError } = await stripe.confirmCardPayment(
          clientSecret,
          { payment_method: ev.paymentMethod.id },
          { handleActions: false }
        );
        if (confirmError) {
          ev.complete('fail');
        } else {
          ev.complete('success');
          if (paymentIntent.status === 'requires_action') {
            const { error } = await stripe.confirmCardPayment(clientSecret);
            if (error) {
              console.log('error', error);
            } else {
            }
          } else {
            console.log('---> startPaidSession  ----  wallet');
            const nameParts = (ev.payerName || '').trim().split(/\s+/);
            const firstName = nameParts[0] || '';
            const lastName = nameParts.slice(1).join(' ');
            await startPaidSession({
              billing: {
                firstName: firstName,
                lastName: lastName,
                email: ev.payerEmail,
                city: ev.paymentMethod.billing_details.address.city,
                state: ev.paymentMethod.billing_details.address.state || 'default',
                country: ev.paymentMethod.billing_details.address.country,
                address1: ev.paymentMethod.billing_details.address.line1,
                address2: ev.paymentMethod.billing_details.address.line2,
                zipCode: ev.paymentMethod.billing_details.address.postal_code
              },
              params: {
                paymentId: paymentIntent.id,
                paymentIntentId: paymentIntentData.paymentIntentId,
                chargePointId: +cp_id,
                idTag: 'web',
                chargePointConnectorId: +connector_id,
                label: ''
              }
            });
          }
        }
      });
    }
  }, [paymentRequest]);

  const handleSwitchTab = (index) => {
    setActiveTabIndex(index);
  };

  // handle payment with normal checkout
  const handleSubmit = async (e) => {
    setErrorText('');
    e.preventDefault();
    if (!stripe || !elements) {
      return;
    }
    setIsProcessing(true);

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: 'if_required'
    });

    if (error) {
      // Handle error here
      setErrorText(error.message);
    } else if (paymentIntent && paymentIntent.status === 'requires_capture') {
      fromCharge
        ? dispatch(setIsButtonLoading({ isLoading: true }))
        : dispatch(setIsButtonLoadingForId({ isLoading: true, cp_id, connector_id }));
      console.log('---> startPaidSession  ----  card');
      await startPaidSession({
        billing: {
          firstName: user.firstName,
          lastName: user.lastName,
          email: userEmail,
          city: user.address.city,
          state: 'RO',
          country: user.address.country,
          address1: user.address.line1,
          address2: user.address.line2,
          zipCode: user.address.postal_code
        },
        params: {
          paymentId: paymentIntent.id,
          paymentIntentId: paymentIntentData.paymentIntentId,
          chargePointId: cp_id,
          idTag: 'web',
          chargePointConnectorId: connector_id,
          label: ''
        }
      });
      setPaymentId(paymentIntent.id);
      onSuccess();
    }
    setIsProcessing(false);
  };

  return (
    <form className='checkout-form'>
      <TabBar activeTabIndex={activeTabIndex} tabs={tabButtons} onClick={setActiveTabIndex} />
      {paymentRequest && (
        <div className={`checkout-form__accordion${activeTabIndex === 0 ? '--active' : ''}`}>
          <PaymentRequestButtonElement options={{ paymentRequest }} />
        </div>
      )}
      <div className={`checkout-form__accordion${activeTabIndex === 1 ? '--active' : ''}`}>
        <h4 className='checkout-form__section-title'>Address</h4>

        <FormInput
          inputClass='checkout__input'
          wrapClass='payment__email'
          id='email'
          type={inputTypes.email}
          label={t('email')}
          value={userEmail}
          placeholder={t('type_the_contact_email')}
          onChange={(e) => setUserEmail(e.target.value)}
        />

        <AddressElement
          options={{
            mode: 'billing',
            fields: {
              phone: 'always'
            },
            display: {
              name: 'split'
            },
            validation: {
              phone: {
                required: 'always'
              }
            }
          }}
          onChange={(event) => {
            if (event.complete) {
              setUser(event.value);
              setHasValidAddress(true);
            } else {
              setHasValidAddress(false);
            }
          }}
        />

        <h4 className='checkout-form__section-title'>Payment</h4>
        <PaymentElement
          onChange={(event) => {
            if (event.complete) {
              setHasValidPayment(true);
            } else {
              setHasValidPayment(false);
            }
          }}
        />
        {errorText && <p className='checkout-form__error'>{errorText}</p>}
        {!paymentId && (
          <Button
            className='checkout-form__submit-button'
            label={isProcessing ? t('is_processing') : t('submit_and_begin_charging')}
            disabled={!(hasValidAddress && hasValidPayment)}
            isLoading={isProcessing}
            onClick={handleSubmit}
          />
        )}
      </div>
    </form>
  );
};

export default CheckoutForm;
